var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { DOB_INPUT_DATE, EMAIL_AUTH_STRATEGY, getConfig, ISO_DATE_FORMAT, parseCustomDate, } from "@jti/fe-common";
import { getUser } from "../../utils";
import { CasBaseApi } from "./CasBaseApi";
import { Endpoints, EvoEndpoints } from "./CasEndpoints";
import { handleAddPlusToPhone } from "./utils";
export var PasswordAction;
(function (PasswordAction) {
    PasswordAction["resetPassword"] = "reset_password";
    PasswordAction["setPassword"] = "set_password";
})(PasswordAction || (PasswordAction = {}));
export class CasAuthApi extends CasBaseApi {
    constructor(baseUrl, tenantToken, ciamToken) {
        super(baseUrl, tenantToken, ciamToken);
        this.tenantToken = tenantToken;
        this.ciamToken = ciamToken;
        this.name = "CasAuthApi";
        this.Endpoints = this.isEvo ? EvoEndpoints : Endpoints;
    }
    static init() {
        const config = getConfig();
        const user = getUser();
        return new CasAuthApi(config.CAS_URL, config.CAS_X_DES_AK_TENANT_TOKEN, user.ciam_token);
    }
    // FIXME: Old CAS validate return 200 which is used in some places, new endpoint returns 204 now, which will break logic in those places
    validate(ciamToken, isCached) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(this.Endpoints.Validate, { body: JSON.stringify({ token: ciamToken }), method: "POST" }, undefined, isCached);
        });
    }
    login(variables) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isEvo) {
                const { defaultDesEvoLoginStrategy } = getConfig();
                const strategy = (_b = (_a = variables.strategy) !== null && _a !== void 0 ? _a : defaultDesEvoLoginStrategy) !== null && _b !== void 0 ? _b : EMAIL_AUTH_STRATEGY;
                const requestBody = {
                    strategy,
                    strategy_data: Object.assign({ identifier: variables.email, password: variables.password }, variables.strategy_data),
                };
                const { data, statusCode } = yield this.request(this.Endpoints.Login, {
                    method: "POST",
                    body: JSON.stringify(requestBody),
                }, true);
                return Promise.resolve({
                    uuid: data.user_uuid,
                    ciam_token: data.token,
                    magento_token: data.magento_token,
                    statusCode,
                });
            }
            return this.request(this.Endpoints.Login, { method: "POST", body: JSON.stringify(variables) }, true);
        });
    }
    /**
     * Registration method
     * @param {RegisterVariables} variables
     * @returns {RegisterVariablesSuccess}
     * @throws {import("./EvoTypes").ValidationResponseError} 422 - Validation Response code
     * @throws {import("./EvoTypes").CommonResponseError} 400,409,424
     */
    register(variables, dobFormat = DOB_INPUT_DATE) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isEvo) {
                const requestBody = {
                    email: variables.email,
                    // NOTE: Phone now is requiring to have + sign (e164 format)
                    phone: variables.phone ? handleAddPlusToPhone(variables.phone) : "",
                    first_name: variables.firstname,
                    last_name: variables.lastname,
                    action_verification_uuids: Object.assign({}, (variables.phone_validation_uuid && { sms: variables.phone_validation_uuid })),
                    age_verification_uuid: variables.age_verification_uuid,
                    consents: variables.consents,
                    custom_attributes: variables.custom_attributes,
                    gender: variables.gender,
                    referral_code: variables.referral_code,
                    source: variables.source,
                    // NOTE: date_of_birth will be having ISO format YYYY-MM-DD (without time) now (partial ISO8601)
                    date_of_birth: parseCustomDate({
                        date: variables.date_of_birth,
                        initialFormat: dobFormat,
                        resultFormat: ISO_DATE_FORMAT,
                    }),
                    strategy: (_a = variables.strategy) !== null && _a !== void 0 ? _a : EMAIL_AUTH_STRATEGY,
                    strategy_data: Object.assign({ identifier: variables.email, password: variables.password }, variables.strategy_data),
                };
                const response = yield this.request(this.Endpoints.Register, {
                    method: "POST",
                    body: JSON.stringify(requestBody),
                }, true);
                return Promise.resolve(Object.assign({ statusCode: response.statusCode }, (response.authentication && {
                    authentication: {
                        uuid: (_b = response.authentication) === null || _b === void 0 ? void 0 : _b.user_uuid,
                        ciam_token: (_c = response.authentication) === null || _c === void 0 ? void 0 : _c.token,
                        magento_token: (_d = response.authentication) === null || _d === void 0 ? void 0 : _d.magento_token,
                    },
                })));
            }
            return this.request(this.Endpoints.Register, {
                method: "POST",
                body: JSON.stringify(variables),
            }, true);
        });
    }
    registerCdpUser(variables) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isEvo) {
                throw new Error("registerCdpUser is not supported in Evo");
            }
            return this.request("/register-cdp-user", {
                method: "POST",
                body: JSON.stringify(variables),
            });
        });
    }
    unregister(uuid, ciamToken) {
        return __awaiter(this, void 0, void 0, function* () {
            if (uuid === undefined) {
                return Promise.reject(new Error("Consumer ID is not defined"));
            }
            const overrideHeaders = ciamToken !== undefined ? { headers: { "X-DES-CONSUMER-IAM-TOKEN": ciamToken } } : {};
            return this.request(this.replaceUuid(this.Endpoints.CancelAccount, uuid), Object.assign({ method: this.isEvo ? "POST" : "PUT" }, overrideHeaders), this.isEvo);
        });
    }
    deactivateAccount(uuid) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isEvo) {
                throw new Error("Deactivation not available for CAS");
            }
            if (uuid === undefined) {
                return Promise.reject(new Error("Consumer ID is not defined"));
            }
            return this.request(`/users/${uuid}/deactivate`, {
                method: "POST",
                // NOTE: This body is required by BE but should not be dynamic
                body: JSON.stringify({ reason: "Hardcoded reason" }),
            });
        });
    }
    resetPassword(email) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/reset-password", {
                method: "POST",
                body: JSON.stringify({
                    email,
                }),
            });
        });
    }
    forgotPassword(email) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(this.Endpoints.ForgotPassword, {
                method: "POST",
                body: JSON.stringify({
                    email,
                }),
            }, true);
        });
    }
    resetConsumerPassword({ newPassword, actionType, ciamToken, confirmPassword, consents, }) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isEvo) {
                return this.request(this.replaceUserToken(this.Endpoints.ResetPassword, ciamToken), {
                    method: "POST",
                    body: JSON.stringify({
                        password: newPassword,
                        password_confirmation: confirmPassword,
                    }),
                }, true);
            }
            return this.request(this.Endpoints.ResetPassword, {
                method: "PUT",
                body: JSON.stringify({
                    new_password: newPassword,
                    token: ciamToken,
                    new_password_confirmation: confirmPassword,
                    action_type: actionType,
                    consents,
                }),
            }, true);
        });
    }
    verifyEmailUpdate(uuidOfRequest) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isEvo) {
                const data = yield this.request(this.Endpoints.VerifyEmailUpdate, {
                    body: JSON.stringify({
                        uuid: uuidOfRequest,
                    }),
                    method: "PATCH",
                }, true);
                return Promise.resolve({
                    personal_info: {
                        firstname: data.first_name,
                        lastname: data.last_name,
                        date_of_birth: data.date_of_birth,
                        email: data.email,
                        gender: data.gender,
                        phone: data.phone,
                    },
                    addresses: data.addresses,
                    statusCode: data.statusCode,
                });
            }
            return this.request(this.Endpoints.VerifyEmailUpdate, {
                body: JSON.stringify({
                    uuid: uuidOfRequest,
                }),
                method: "PATCH",
            });
        });
    }
    verifyConsumer(email) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(this.replaceEmail(this.Endpoints.VerifyEmail, email), {}, true);
        });
    }
    getPasswordRequirements() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isEvo) {
                const { password_requirements, statusCode } = yield this.request(this.Endpoints.PasswordRequirements, { method: "GET" }, true);
                return Promise.resolve({
                    statusCode,
                    minLength: password_requirements.min_length,
                    minLowerCase: password_requirements.min_lower_case,
                    minUpperCase: password_requirements.min_upper_case,
                    minNumber: password_requirements.min_number,
                    minSymbol: password_requirements.min_symbol,
                    excludeUsername: password_requirements.exclude_username,
                });
            }
            return this.request(this.Endpoints.PasswordRequirements, {
                method: "GET",
            });
        });
    }
    dsarRequest(variables) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestBody = this.isEvo
                ? {
                    first_name: variables.firstName,
                    last_name: variables.lastName,
                    email: variables.email,
                    language: variables.language,
                    request_types: variables.requestTypes,
                    market: variables.market,
                    country: variables.country,
                }
                : variables;
            return this.request(this.Endpoints.Privacy, {
                method: "POST",
                body: JSON.stringify(requestBody),
            }, true);
        });
    }
    getAvailableAuthMethods() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isEvo) {
                const { available_authentication_strategies, statusCode } = yield this.request(this.Endpoints.MarketCustomAttrsUrl, { method: "GET" }, true);
                return Promise.resolve(Object.assign(Object.assign({}, available_authentication_strategies), { statusCode }));
            }
            throw new Error("Getting available auth methods is not supported in CAS");
        });
    }
}
//# sourceMappingURL=CasAuthApi.js.map